import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { H1, H2, H3, Body } from '../components/ui/Typography';

export default function MediaKit() {
  const colors = [
    {
      name: 'Primary Orange',
      hex: '#F97316',
      description: 'Our main accent color, used for call-to-action buttons and important interactive elements.'
    },
    {
      name: 'Primary Green',
      hex: '#059669',
      description: 'Our primary brand color, used for headings, important text, and key numerical data.'
    },
    {
      name: 'Light Orange',
      hex: '#FB923C',
      description: 'Used for secondary elements, hover states, and subtle highlights.'
    },
    {
      name: 'Light Green',
      hex: '#34D399',
      description: 'Complementary to our primary green, used for secondary elements and visual accents.'
    },
    {
      name: 'Dark Orange',
      hex: '#EA580C',
      description: 'Reserved for elements requiring high contrast and emphasis.'
    },
    {
      name: 'Dark Green',
      hex: '#047857',
      description: 'Used for text and elements that need stronger contrast against light backgrounds.'
    },
    {
      name: 'Text Color',
      hex: '#1e293b',
      description: 'Standard text color for optimal readability.'
    },
    {
      name: 'Background Color',
      hex: '#F8FAFC',
      description: 'Default background color for maintaining visual consistency.'
    }
  ];

  const typographyStyles = [
    { name: 'H1 (Page Titles)', font: 'Kanit Bold', size: '48px', lineHeight: '56px' },
    { name: 'H2 (Section Titles)', font: 'Kanit Bold', size: '36px', lineHeight: '44px' },
    { name: 'H3 (Sub-sections)', font: 'Kanit Bold', size: '22px', lineHeight: '36px' },
    { name: 'H4 (Card titles)', font: 'Inter Medium', size: '20px', lineHeight: '32px' },
    { name: 'H5 (Small titles)', font: 'Inter Medium', size: '18px', lineHeight: '28px' },
    { name: 'Large body', font: 'Inter Medium', size: '16px', lineHeight: '28px' },
    { name: 'Regular body', font: 'Inter Medium', size: '16px', lineHeight: '24px' },
    { name: 'Small body/captions', font: 'Inter Medium', size: '14px', lineHeight: '20px' },
    { name: 'Mini text (footnotes)', font: 'Inter Medium', size: '12px', lineHeight: '16px' }
  ];

  return (
    <>
      <Helmet>
        <title>RentFi | Media Kit</title>
        <meta 
          name="description" 
          content="Access RentFi's official brand resources, including logos, color palette, typography guidelines, and media assets for content creators, journalists, and partners." 
        />
        <meta 
          name="keywords" 
          content="RentFi media kit, brand guidelines, press resources, brand assets, logo downloads, brand colors, typography" 
        />
        <link rel="canonical" href="https://rentfi.io/media-kit" />
      </Helmet>
      <div className="min-h-screen bg-gray-50 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="text-center mb-16">
              <H1 className="text-primary mb-4">RentFi Media Kit</H1>
              <Body className="text-gray-600 max-w-3xl mx-auto mb-8">
                Welcome to RentFi's official media and brand resources. This guide is designed to help content creators, journalists, and partners maintain consistency when representing RentFi across various media channels.
              </Body>
              <div className="max-w-2xl mx-auto">
                <Body className="text-gray-600">
                  Whether you're writing an article, creating content, or developing marketing materials, these guidelines will help ensure our brand is represented accurately and consistently.
                </Body>
              </div>
            </div>

            {/* Color Palette Section */}
            <div className="bg-white rounded-xl shadow-lg p-8 mb-16">
              <H2 className="text-primary mb-8">Color Palette</H2>
              <div className="mb-6">
                <Body className="text-gray-600">
                  Our color palette has been carefully selected to reflect RentFi's values of trust, innovation, and accessibility. When creating content, please use these colors to maintain brand consistency across all materials.
                </Body>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {colors.map((color) => (
                  <div key={color.hex} className="flex items-start space-x-4">
                    <div 
                      className="w-16 h-16 rounded-lg shrink-0" 
                      style={{ backgroundColor: color.hex }}
                    />
                    <div>
                      <H3 className="text-gray-900 mb-1">{color.name}</H3>
                      <code className="text-sm bg-gray-100 px-2 py-1 rounded">{color.hex}</code>
                      <Body className="text-gray-600 mt-2">{color.description}</Body>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Typography Section */}
            <div className="bg-white rounded-xl shadow-lg p-8 mb-16">
              <H2 className="text-primary mb-8">Typography</H2>
              <div className="mb-6">
                <Body className="text-gray-600">
                  Our typography system combines Kanit for headlines and Inter for body text, creating a clear visual hierarchy that enhances readability and brand recognition.
                </Body>
              </div>
              
              <div className="space-y-8">
                <div>
                  <H3 className="text-gray-900 mb-4">Primary Font: Kanit</H3>
                  <div className="font-kanit text-4xl">
                    ABCDEFGHIJKLM<br />
                    abcdefghijklmopqrz<br />
                    0123456789
                  </div>
                </div>

                <div>
                  <H3 className="text-gray-900 mb-4">Secondary Font: Inter</H3>
                  <div className="font-inter text-4xl">
                    ABCDEFGHIJKLM<br />
                    abcdefghijklmopqrz<br />
                    0123456789
                  </div>
                </div>

                <div>
                  <H3 className="text-gray-900 mb-4">Typography Scale</H3>
                  <div className="space-y-6">
                    {typographyStyles.map((style) => (
                      <div key={style.name} className="border-b border-gray-100 pb-4">
                        <div className="flex justify-between items-baseline mb-2">
                          <H3 className="text-gray-900">{style.name}</H3>
                          <code className="text-sm bg-gray-100 px-2 py-1 rounded">
                            {style.size} / {style.lineHeight}
                          </code>
                        </div>
                        <Body className="text-gray-600">
                          Font: {style.font}
                        </Body>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Download Section */}
            <div className="bg-white rounded-xl shadow-lg p-8 text-center">
              <H2 className="text-primary mb-4">Download Resources</H2>
              <Body className="text-gray-600 mb-8">
                Our comprehensive press kit, including high-resolution logos, brand assets, and detailed guidelines.
              </Body>
              <a
                href="https://rebrand.ly/79rrkv2"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-cta"
                aria-label="Download RentFi's comprehensive press kit including logos and brand assets"
              >
                Download Press Kit
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
}